import { Help } from "@mui/icons-material";
import {
  Avatar,
  Box,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../assets/styles/plans.css";
import countriesData from "../data/countriesList.json";
import { gateway } from "../gateway";
import { nextViewConnectBusinessState, updateConnectBusinessState } from "../redux/features/connectBusinessStateSlice";
import { handleRefreshToken } from "../utils";
import { SnackbarAlert } from "./common/SnackbarAlert";
import IconButtonFileUpload, { blobUrlToFile } from "./ImageUploader";
import { AccountDeactivation } from "./informationBtnBolean";
import { LoaderComponent } from "./Loader";
import { MapPredictive } from "./mapPredictive";
import { MapPredictiveState } from "./mapPredictiveState";
import { ButtonsHoursOperation } from "./ButtonsHoursOperation";

const formatPhoneNumber = (number) => {
  if (number?.length >= 7) {
    return `(${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
  } else if (number?.length >= 4) {
    return `(${number.slice(0, 3)}) ${number.slice(3)}`;
  } else if (number?.length >= 1) {
    return `(${number}`;
  }
  return "";
};

export const ConnectEmployeeAccount = ({ updateComponentState = () => { }, componentState }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userState = useSelector((state) => state.userState);
  const tokenLocal = sessionStorage.getItem("token");

  const [myUserInfo, setMyUserInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const [phone, setPhone] = useState("");
  const [age, setAge] = useState("");
  const [ageError, setAgeError] = useState("");
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  const [changes, setChanges] = useState(false);
  const [cancel, setCancel] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("");

  const countries = countriesData;
  const [isCodesOpen, setIsCodesOpen] = useState(false);
  const [countryCode, setCountryCode] = useState("");
  const [formatNumber, setFormatNumber] = useState("");

  const handleLocationSelect = (city, state, zipCode, country, address) => {
    // Actualiza los estados
    setCity(city);
    setState(state);
    setZipCode(zipCode);
    setCountry(country);
    // Más lógica si es necesario
  };

  const handleStateSelect = (state, country) => {
    setState(state);
    setCountry(country);
  };

  const isSomeEmpty = (listToCheck = []) => {
    const data = listToCheck.some((item) => item === "" || item === null);
    console.log(data);
    return data;
  };

  const getMyUserInfo = async () => {
    try {
      const response = await gateway.get(`/users/self`, {
        headers: {
          Authorization: `${tokenLocal}`,
        },
      });
      const data = response.data.data;
      setMyUserInfo(data);
      dispatch(updateComponentState(data));
    } catch (error) {
      console.error(error);
    }
  };

  const saveMyUserInfo = async () => {
    try {
      setOpen(true);
      const file = await blobUrlToFile(userState.imageUrl);
      const formData = new FormData();

      formData.append("age", age);
      formData.append("phone", phone);
      formData.append("address", userState.address ?? "");
      formData.append("country", country);
      formData.append("state", state);
      formData.append("city", city);
      formData.append("zipCode", zipCode);
      formData.append("countryCode", countryCode);
      formData.append("firstName", firstName);
      formData.append("lastName", lastName);

      if (userState.imageUrl) {
        formData.append("imageFile", file);
      }

      const response = await gateway.put("/users/self", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `${tokenLocal}`,
        },
      });
      if (response.status === 200) {
        await handleRefreshToken();
      }
      const data = response.data.data;
      dispatch(updateComponentState(data));
      setMyUserInfo(data);
      setAlertMessage("Information updated");
      setAlertSeverity("success");
    } catch (error) {
      console.error(error);
      setAlertMessage("Error updating your information: " + error?.message);
      setAlertSeverity("error");
    } finally {
      setOpen(false);
      setChanges(false);
      setCancel(false);
      setIsEmpty(false);
      setOpenAlert(true);
    }
  };

  const handleUpdate = async () => {
    await saveMyUserInfo();
  };

  useEffect(() => {
    handleUserInfo();
  }, []);
  useEffect(() => {
    if (myUserInfo) {
      setPhone(myUserInfo?.phone);
      setFormatNumber(formatPhoneNumber(myUserInfo?.phone));
      setAge(myUserInfo.age);
      setCountry(myUserInfo.country);
      setState(myUserInfo.state);
      setCity(myUserInfo.city);
      setZipCode(myUserInfo.zipCode);
      setCountryCode(myUserInfo.countryCode ? myUserInfo.countryCode : "USA-1");
      setFirstName(myUserInfo.firstName);
      setLastName(myUserInfo.lastName);
      setEmail(myUserInfo.email);
    }
  }, [myUserInfo]);

  useEffect(() => {
    setIsEmpty(isSomeEmpty([state, city, zipCode, phone, country]));
  }, [state, city, zipCode, phone, country]);

  useEffect(() => {
    if (cancel) {
      getMyUserInfo(); // Esta función ya obtiene los servicios y actualiza los estados.
      dispatch(updateComponentState({ imageUrl: null }));
      setCancel(false);
      setChanges(false);
    }
  }, [cancel]);

  const handleUserInfo = async () => {
    try {
      setOpen(true);
      await getMyUserInfo();
    } catch (error) {
      console.log(error);
    } finally {
      setOpen(false);
    }
  };

  const handleInputChange = (e) => {
    setChanges(true);
    const { name, value } = e.target;
    switch (name) {
      case "age":
        setAge(value);

        if (!Number.isInteger(Number(value)) || value < 0 || value > 150) {
          setAgeError("Please enter a valid age");
        } else {
          setAgeError("");
        }

        break;
      case "country":
        setCountry(value);
        break;
      case "state":
        setState(value);
        break;
      case "city":
        setCity(value);
        break;
      case "zipCode":
        setZipCode(value);
        break;
      case "firstName":
        setFirstName(value);
        break;
      case "lastName":
        setLastName(value);
        break;
      default:
        break;
    }
    dispatch(updateConnectBusinessState({ [name]: value }));
  };

  const handleChangeNumber = (e) => {
    const phoneVal = e?.target?.value;
    const validInput = phoneVal?.replace(/[^0-9]/g, "");
    if (validInput === phone) return;
    if (validInput.length === 10) setChanges(true);
    setFormatNumber(formatPhoneNumber(validInput));
    setPhone(validInput);
    dispatch(updateConnectBusinessState({ phone: validInput }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setOpen(true);

    const userData = {
      phone: phone,
      age: age,
      country: country,
      state: state,
      city: city,
      zipCode: zipCode,
    };

    dispatch(updateConnectBusinessState(userData));
    dispatch(nextViewConnectBusinessState(userData));

    setIsLoading(false);
    setOpen(false);
  };

  const handleCountryCode = (event) => {
    setCountryCode(event?.target?.value);
    setChanges(true);
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        component="form"
        style={{
          borderRadius: "10px",
          backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
          display: "flex",
          flexDirection: "column",
          padding: "20px",
          alignItems: "center",
          transition: "opacity 1s ease-in-out",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
          height: "70%",
        }}
        sx={{
          marginTop: { xs: 8, md: 9 },
          width: { xs: "100%", md: 600 },
        }}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            sm={12}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              style={{
                fontFamily: "Tahoma, sans-serif",
                fontWeight: "bold",
                color: "White",
                background: "var(--fx-surface-sidebar, #233044)",
                padding: "10px",
                width: "100%",
                marginBottom: "10px",
                borderRadius: "10px",
                marginTop: "10px",
                textAlign: "center",
              }}
            >
              Update your information
            </Typography>
            <Avatar sx={{ width: "90px", height: "90px" }} src={userState.imageUrl ?? userState.avatar} />
            <IconButtonFileUpload setChanges={setChanges} updateComponentState={updateComponentState} />

            {/* Age TextField */}
            <TextField
              margin="normal"
              id="age"
              type="text"
              label="Age (Optional)"
              InputLabelProps={{ shrink: age ? true : false }}
              name="age"
              size="small"
              variant="outlined"
              onChange={handleInputChange}
              error={!!ageError}
              helperText={ageError}
              value={age}
              sx={{
                fontSize: "12px",
                width: "100%",
                mb: 0.5,
              }}
            />

            <Box sx={{ display: "flex", width: "100%", mb: -1 }}>
              <TextField
                margin="normal"
                id="firstName"
                label="First Name"
                name="firstName"
                size="small"
                InputLabelProps={{ shrink: city ? true : false }}
                variant="outlined"
                onChange={handleInputChange}
                value={firstName}
                sx={{
                  fontSize: "12px",
                  width: "100%",
                  mr: 1,
                }}
              ></TextField>
              <TextField
                margin="normal"
                id="lastName"
                label="Last Name"
                name="lastName"
                size="small"
                InputLabelProps={{ shrink: city ? true : false }}
                variant="outlined"
                onChange={handleInputChange}
                value={lastName}
                sx={{
                  fontSize: "12px",
                  width: "100%",
                }}
              ></TextField>
            </Box>

            <Tooltip title="Contact support to change your email: support@connectacleaner.com">
              <TextField
                margin="normal"
                id="email"
                label="Email"
                name="email"
                size="small"
                variant="outlined"
                disabled
                fullWidth
                value={email}
                sx={{
                  fontSize: "12px",
                  mb: { md: 2, xs: 1 },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton>
                        <Help sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              ></TextField>
            </Tooltip>

            <Box sx={{ display: "flex", width: "100%", mt: { xs: 0.5, md: 0 } }}>
              <FormControl variant="outlined" sx={{ width: "auto", mr: 1 }}>
                <Select
                  value={countryCode}
                  onChange={handleCountryCode}
                  onOpen={() => setIsCodesOpen(true)}
                  onClose={() => setIsCodesOpen(false)}
                  name="countryCode"
                  variant="outlined"
                  fullWidth
                  size="small"
                  renderValue={(selected) => {
                    const selectedCountry = countries.find((data) => `${data.alpha3}-${data.numberPrefix}` === selected);
                    return selectedCountry ? (
                      <Box sx={{ display: "flex", alignItems: "center", pr: { xs: selectedCountry.numberPrefix.length > 2 ? 8.5 : 5, md: 4 } }}>
                        <img src={selectedCountry.file_url} alt={selectedCountry.name} width="20" style={{ marginRight: 5 }} />+
                        {selectedCountry.numberPrefix}
                      </Box>
                    ) : (
                      ""
                    );
                  }}
                >
                  {countries.map((data) => (
                    <MenuItem key={data.alpha3} value={`${data.alpha3}-${data.numberPrefix}`}>
                      <ListItemIcon>
                        <img src={data.file_url} alt={data.name} width="22" />
                      </ListItemIcon>
                      {isCodesOpen ? (
                        <ListItemText primary={`${data.name} +${data.numberPrefix}`} />
                      ) : (
                        <ListItemText primary={`+${data.numberPrefix}`} />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                id="phone"
                type="phone"
                label="Phone Number"
                name="phone"
                size="small"
                InputLabelProps={{ shrink: phone ? true : false }}
                error={phone && phone?.length !== 10}
                helperText={phone && phone?.length !== 10 ? "Phone number must be 10 digits" : ""}
                variant="outlined"
                inputProps={{ maxLength: 14 }}
                onChange={handleChangeNumber}
                value={formatNumber}
                sx={{
                  fontSize: "12px",
                  width: "100%",
                }}
                autoFocus
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                width: "100%",
                mt: 2,
              }}
            >
              <MapPredictive
                setChanges={setChanges}
                onLocationSelect={handleLocationSelect}
                updateComponentState={updateComponentState}
                componentState={componentState}
                requiredAddress={false}
              ></MapPredictive>
            </Box>

            <TextField
              margin="normal"
              id="city"
              label="City"
              name="city"
              size="small"
              InputLabelProps={{ shrink: city ? true : false }}
              variant="outlined"
              onChange={handleInputChange}
              value={city}
              sx={{
                fontSize: "12px",
                width: "100%",
              }}
            />

            {/* State TextField */}
            <MapPredictiveState onStateSelect={handleStateSelect} setChanges={setChanges}></MapPredictiveState>

            {/* Zip Code TextField */}
            <TextField
              margin="normal"
              id="zipCode"
              label="Zip Code"
              name="zipCode"
              size="small"
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              onChange={handleInputChange}
              value={zipCode}
              sx={{
                fontSize: "12px",
                width: "100%",
              }}
            />

            <TextField
              margin="normal"
              id="country"
              label="Country"
              name="country"
              size="small"
              InputLabelProps={{ shrink: country ? true : false }}
              variant="outlined"
              onChange={handleInputChange}
              value={country}
              sx={{
                fontSize: "12px",
                width: "100%",
                mt: 1,
              }}
            />
          </Grid>
        </Grid>
        <Grid item>
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <ButtonsHoursOperation
                disabled={!changes || !!ageError || isEmpty}
                setCancel={setCancel}
                onSubmit={handleUpdate}
              ></ButtonsHoursOperation>
            </Grid>
          </Grid>
        </Grid>
        <Grid container spacing={2} justifyContent="center">
          <AccountDeactivation></AccountDeactivation>
        </Grid>
      </Box>
      <LoaderComponent open={open}></LoaderComponent>
      <SnackbarAlert
        openState={openAlert}
        setOpenState={setOpenAlert}
        severity={alertSeverity}
        duration={4500}
        message={alertMessage}
      ></SnackbarAlert>
    </div>
  );
};
